import React, { useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import Modal from "src/components/ui/Modal";
import Form, { FormField } from "src/components/ui/Form";
import SearchInput, { SearchInputOption } from "src/components/ui/SearchInput";

import { useAppDispatch, useAppSelector } from "src/redux/hooks";
import * as transactionActions from "src/redux/slices/transactionSlice";
import tenantApis from "src/api/tenant";
import { Tenant } from "src/types/tenant.types";
import { transactionStatuses, transactionTypes } from "src/constants/common";

interface TransactionCreateModalProps {
  open: boolean;
  onClose: () => void;
}

const TransactionCreateModal: React.FC<TransactionCreateModalProps> = ({
  open,
  onClose,
}) => {
  const dispatch = useAppDispatch();
  const { createTransaction } = useAppSelector((state) => state.transaction);
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [tenantId, setTenantId] = useState("");
  const [tenantLabel, setTenantLabel] = useState("");

  useEffect(() => {
    if (createTransaction.data) {
      toast.success("Transaction recorded successfully");
      dispatch(transactionActions.resetCreateTransaction());
      onClose();
    } else if (createTransaction.error) {
      toast.error(`Error: ${createTransaction.error}`);
      dispatch(transactionActions.resetCreateTransaction());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createTransaction]);

  const fields: FormField[] = useMemo(() => {
    const baseFields: FormField[] = [
      {
        label: "Transaction Type",
        name: "transactionType",
        type: "dropdown",
        options: transactionTypes.map((type) => ({ label: type, value: type })),
        defaultValue: "payment",
        validations: { required: true },
      },
      {
        label: "Payment Method",
        name: "paymentMethod",
        type: "dropdown",
        options: [
          { label: "Cash", value: "cash" },
          { label: "Online", value: "online" },
        ],
        defaultValue: "cash",
        validations: { required: true },
        onChange: (value) => setPaymentMethod(value),
      },
      {
        label: "Amount",
        name: "amount",
        type: "number",
        placeholder: "Enter Amount",
        validations: { required: true },
      },
      {
        label: "Status",
        name: "status",
        type: "dropdown",
        options: transactionStatuses.map((status) => ({
          label: status,
          value: status,
        })),
        defaultValue: "created",
        validations: { required: true },
      },
      {
        label: "Remarks",
        name: "remarks",
        type: "textarea",
        placeholder: "Add remarks (optional)",
      },
    ];

    if (paymentMethod === "online") {
      baseFields.splice(2, 0, {
        label: "Payment Mode",
        name: "paymentMode",
        type: "dropdown",
        options: [
          { label: "PhonePe", value: "phonepay" },
          { label: "GPay", value: "gpay" },
          { label: "Paytm", value: "paytm" },
          { label: "Other", value: "other" },
        ],
        validations: { required: true },
      });
      baseFields.splice(3, 0, {
        label: "Reference Id",
        name: "referenceId",
        type: "text",
        placeholder: "Enter Reference Id",
        validations: { required: true },
      });
    }

    return baseFields;
  }, [paymentMethod]);

  const handleSubmit = (data: Record<string, any>) => {
    const payload = { ...data };
    if (payload.paymentMethod !== "online") {
      delete payload.paymentMode;
      delete payload.referenceId;
    }
    if (tenantId) payload.tenantId = tenantId;
    dispatch(transactionActions.createTransactionRequest(payload));
  };

  const handleClose = () => {
    setTenantId("");
    setTenantLabel("");
    onClose();
  };

  return (
    <Modal open={open} onClose={handleClose} title="Add Transaction" size="lg">
      <div className="space-y-4">
        <div>
          <SearchInput<Tenant>
            label="Tenant (optional)"
            placeholder="Search tenants by name…"
            value={tenantLabel}
            onSearch={async (query) => {
              const res: any = await tenantApis.getTenantList({
                page: 1,
                limit: 10,
                search: query,
              });
              const list: Tenant[] = res?.data?.data || res?.data || [];
              return list.map((t) => ({
                value: t._id || "",
                label: t.tenantName || "",
                data: t,
              }));
            }}
            onSelect={(option: SearchInputOption<Tenant>) => {
              setTenantId(option.value);
              setTenantLabel(option.label);
            }}
            onClear={() => {
              setTenantId("");
              setTenantLabel("");
            }}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Who this transaction is for. Leave blank if it isn't tied to a
            specific tenant.
          </p>
        </div>

        <Form
          fields={fields}
          onSubmit={handleSubmit}
          onCancel={handleClose}
          submitLabel="Create Transaction"
          loading={createTransaction.loading}
        />
      </div>
    </Modal>
  );
};

export default TransactionCreateModal;
